import {ErrorText, GridCol, GridForm, GridRow, GridRowSingleCol, HelpText, NotSetText} from './grid-form'
import {ImageCarousel} from './ImageCarousel'
import AioSelect from './aio-inputs/AioSelect'
import icons from '../../markons/markons'
import _ from 'lodash'
import Step from './Step'
import type {ReactElement} from 'react';
import React, { useMemo, useRef, useState} from 'react'
import type {Store} from '../helpers/react-form'
import type {FlightInfo, SegmentBagsValue} from '../models/Segment';
import SegmentModel from '../models/Segment'
import {SpinnerButton} from './SaveSubmitButton'
import css from './FreeQuoteForm.less'
import aioCss from './aio-inputs/aio.less'
import cpp from './CustomerPaymentPage.less'
import ActionButton from './ActionButton'
import ContentIndent from './ContentIndent'
import Icon from './Icon'
import type {OptionTuples} from '../helpers/react-helpers'
import RadioGroup from './RadioGroup'
import RadioButton from './RadioButton'
import assert from 'assert'
import Segment from './Segment'
import ActionLink from './ActionLink'
import type {Company} from './CustomerPaymentCommon'
import { Markup } from 'interweave';
import {CompanyBlock} from './CompanyBlock'
import {BlockSpacer} from './spacer'
import cn from 'classnames'
import StopModel from '../models/Stop'
import update from 'immutability-helper2'
import AioTextArea from './aio-inputs/AioTextArea'
import AioText from './aio-inputs/AioText'
import AioPhoneNumber from './aio-inputs/AioPhoneNumber'
import kb, {columnName, makeFullName, navigate, numberFormat} from '../kb'
import {combineDateAndTime, parseDate, parseTime} from '../util/parse-time'
import {formatTime, reformatDate} from '../util/format-time'
import {arrayMap, fpPick, groupBy, idMap, mapDefined, mapMap} from '../util/collections'
import {addMinutes} from 'date-fns/fp'
import {formatAddress, formatBookingStop, stopIsValid} from '../google-maps'
import ContentSection from './ContentSection'
import {useSaveHandler} from '../helpers/react-hooks'
import type { ValidationErrors} from './ErrorMessage';
import {FlattenedErrorMessage} from './ErrorMessage'
import type {AvailableVehicle} from '../data/vehicles';
import { useAvailableVehicles} from '../data/vehicles'
import LoadingDots from './LoadingDots'
import type { CustomerInfo, FormDataType} from './FreeQuoteFormState';
import {currentVehicleStore, formState} from './FreeQuoteFormState'
import {useMountEffect} from '../hooks/useMountEffect'
import {usePostServerDataDebounced} from '../hooks/useServerData'
import type {DollarAmount} from '../data/currency-map'
import {isEmptyInput} from '../util/input'
import {parseInt} from '../util/math'
import {EMAIL_REGEX} from '../data/validation'
import AioCheckbox from './aio-inputs/AioCheckbox'
import NumberBox from './NumberBox'
import {SimpleTable} from './tables'


type CurrencyAmount = {
    decimal: string
    currency: string
    value: number
    formatted: string
}

type OptionalFee = {
    id: StringNum
    name: string
    price: string
    type: 'FIXED'|'PERCENT'
    maxQuantity: number | null
}

/**
 * @see QuotesController::freeQuoteForm
 */
type FreeQuoteFormProps = {
    company: Company
    occasions: OptionTuples
    phoneTypes: OptionTuples
    googleMapsApiKey: string
    companyCode: string
    distanceUnit: string
    distanceRounding: string
    /** @deprecated `redirectUrl` will come from the server on submit */
    redirectUrl: string
    distanceNearest: number
    instantQuoteEnabled: boolean
    disclaimer: string
    instantPayEnabled: boolean
    customer?: CustomerInfo
    existingCustomerId?: StringNum
    optionalFees: OptionalFee[]
};


interface TripDetailsProps {
    store: Store<FormDataType>
    occasions: OptionTuples
}


const TRIP_TYPE_MAP = new Map([
    ['ONE_WAY', "One Way"],
    ['ROUND_TRIP', "Round Trip"],
    ['AS_DIRECTED', "As Directed/Hourly"],
])

interface ContactInfoProps {
    phoneTypes: OptionTuples
    existingCustomer?: boolean
}

function ContactInfo({phoneTypes,existingCustomer}: ContactInfoProps) {
    const store = formState.useFormStore()
    return (
        <>
            <Step title="Your Information" grid>
                <GridRow>
                    <GridCol minWidth={180}>
                        <AioText required label="First Name" icon={icons.faUser} {...store.input('customer.first_name')} disabled={existingCustomer}/>
                    </GridCol>
                    <GridCol minWidth={180}>
                        <AioText label="Last Name" icon={icons.faUser} {...store.input('customer.last_name')} disabled={existingCustomer}/>
                    </GridCol>
                </GridRow>
                <GridRow>
                    <GridCol minWidth={180}>
                        <AioText label="Company Name" icon={icons.faBuilding} {...store.input('customer.company')} disabled={existingCustomer}/>
                    </GridCol>
                    <GridCol minWidth={180}>
                        <AioText required label="Email Address" icon={icons.faEnvelope} {...store.input('customer.email')} disabled={existingCustomer} inputMode="email"/>
                    </GridCol>
                </GridRow>
                <GridRow>
                    <GridCol minWidth={225}>
                        <AioPhoneNumber numberValue={store.get('customer.pri_phone_number')}
                                        typeValue={store.get('customer.pri_phone_type')}
                                        numberOnChange={ev => store.set('customer.pri_phone_number', ev.target.value)}
                                        typeOnChange={ev => store.set('customer.pri_phone_type', ev.target.value)}
                                        label="Primary Phone Number" typeOptions={phoneTypes} disabled={existingCustomer}/>
                    </GridCol>
                    <GridCol minWidth={225}>
                        <AioPhoneNumber numberValue={store.get('customer.sec_phone_number')}
                                        typeValue={store.get('customer.sec_phone_type')}
                                        numberOnChange={ev => store.set('customer.sec_phone_number', ev.target.value)}
                                        typeOnChange={ev => store.set('customer.sec_phone_type', ev.target.value)}
                                        label="Secondary Phone Number" typeOptions={phoneTypes} disabled={existingCustomer}/>
                    </GridCol>
                </GridRow>
                <GridRowSingleCol>
                    <label>Are you also the passenger?</label>
                    <RadioGroup {...store.input('passenger_copy')}>
                        <RadioButton value="SAME" icon={icons.miscCheck}>Yes</RadioButton>
                        <RadioButton value="DIFFERENT" icon={icons.miscCross}>No</RadioButton>
                    </RadioGroup>
                </GridRowSingleCol>
            </Step>
            {store.data.passenger_copy !== 'DIFFERENT' ? null : <Step title="Passenger Information" grid>
                <GridRow>
                    <GridCol>
                        <AioText label="First Name"
                                 icon={icons.faUser} {...store.input('passenger_first_name')} />
                    </GridCol>
                    <GridCol>
                        <AioText label="Last Name"
                                 icon={icons.faUser} {...store.input('passenger_last_name')} />
                    </GridCol>
                </GridRow>

                <GridRow>
                    <GridCol minWidth={225}>
                        <AioPhoneNumber numberValue={store.get('passenger_pri_phone_number')}
                                        typeValue={store.get('passenger_pri_phone_type')}
                                        numberOnChange={ev => store.set('passenger_pri_phone_number', ev.target.value)}
                                        typeOnChange={ev => store.set('passenger_pri_phone_type', ev.target.value)}
                                        label="Primary Phone Number" typeOptions={phoneTypes}/>
                    </GridCol>
                    <GridCol minWidth={225}>
                        <AioPhoneNumber numberValue={store.get('passenger_sec_phone_number')}
                                        typeValue={store.get('passenger_sec_phone_type')}
                                        numberOnChange={ev => store.set('passenger_sec_phone_number', ev.target.value)}
                                        typeOnChange={ev => store.set('passenger_sec_phone_type', ev.target.value)}
                                        label="Primary Phone Number" typeOptions={phoneTypes}/>
                    </GridCol>
                </GridRow>

                <GridRowSingleCol>
                    <AioText label="Email Address" icon={icons.faEnvelope} {...store.input('passenger_email')} inputMode="email"/>
                </GridRowSingleCol>
            </Step>}
        </>
    )
}


interface TripItineraryProps {
    distanceUnit: string
    googleMapsApiKey: string
    occasions: OptionTuples
}

function TripItinerary({distanceUnit, googleMapsApiKey, occasions}: TripItineraryProps) {
    const store = formState.useFormStore()
    return (
        <>
            <Step title="Itinerary">
                <div className="segment-container">
                    {store.get('segments', []).map((seg, segIdx) => (
                        <Segment key={seg.key}
                                 value={seg}
                                 onChange={seg => store.update({segments: {[segIdx]: {$merge: seg}}})}
                                 number={segIdx + 1}
                                 addStop={stopIdx => ev => {
                                     store.update({segments: {[segIdx]: {stops: {$splice: [[stopIdx, 0, new StopModel()]]}}}})
                                 }}
                                 removeStop={stopIdx => ev => {
                                     store.update({
                                         segments: {
                                             [segIdx]: {
                                                 stops: {
                                                     $apply: (stops: StopModel[]) => {
                                                         if(stops.length <= 2) {
                                                             return update(stops, {[stopIdx]: {$set: new StopModel()}})
                                                         }
                                                         return update(stops, {$splice: [[stopIdx, 1]]})
                                                     }
                                                 }
                                             }
                                         }
                                     })
                                 }}
                                 removeSegment={ev => {
                                     store.update({segments: {$splice: [[segIdx, 1]]}})
                                 }}
                                 googleMapsApiKey={googleMapsApiKey}
                                 quoteRequest
                                 billingType={null}
                                 distanceUnit={distanceUnit}
                                 militaryTime={false}
                        />
                    ))}
                </div>

                <div className="centered-row">
                    <div>
                        <ActionLink testId="add-segment" className="btn btn-new left" onClick={() => {
                            store.update({segments: {$push: [new SegmentModel()]}})
                        }}><Icon
                            name={icons.ionAndroidAddCircle}/> Add Segment</ActionLink>
                        <ActionLink testId="add-reverse-route" className="btn btn-new left" onClick={() => {
                            store.update({
                                segments: {
                                    $apply: (segments: SegmentModel[]) => {
                                        return [
                                            ...segments,
                                            ...segments.map(seg => new SegmentModel({
                                                ...seg,
                                                date: "",
                                                time: "",
                                                id: null,
                                                stops: seg.stops.map(stop => new StopModel({
                                                    ...stop,
                                                    id: null,
                                                })).reverse(),
                                            })).reverse()
                                        ]
                                    }
                                }
                            })

                        }}><Icon
                            name={icons.faRetweet}/> Add Reverse Route</ActionLink>
                    </div>
                </div>
            </Step>
            <Step title="Details" grid>
                <GridRow>
                    <GridCol minWidth={250}>
                        <AioSelect required label="Trip Type"
                                   icon={icons.miscMapMarker} {...store.input('trip_type')}
                                   placeholder="— Trip Type —">
                            {mapMap(TRIP_TYPE_MAP, (text, key) => <option key={key} value={key}>{text}</option>)}
                        </AioSelect>
                    </GridCol>

                    <GridCol minWidth={180}>
                        <AioSelect label="Occasion"
                                   icon={icons.faBirthdayCake} {...store.input('occasion_id')}
                                   options={occasions} placeholder="— Occasion —"/>
                    </GridCol>

                    <GridCol minWidth={140}>
                        <AioSelect label="Total Passengers"
                                   icon={icons.faUserFriends} {...store.input('passenger_count')}
                                   options={_.range(1, 201)} placeholder="— Total Passengers —"/>
                    </GridCol>
                </GridRow>
            </Step>
        </>
    )
}

interface VehicleSelectionProps {
    companyCode: string
}

interface VehicleItemProps {
    selected?: boolean
    passengerCount?: number
    vehicle: AvailableVehicle
    onSelect?(vehicleId: StringNum): void
}



function HourlyRate(amount: DollarAmount) {
    const currencyFormatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: amount.currency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0,
    })

    return <div className={css.vehicleHourlyBox}>
        <span className={css.vehicleHourlyRate}>{currencyFormatter.format(amount.value) || amount.formatted}</span>
        <span className={css.perHour}>Per Hour</span>
    </div>
}

function VehicleItem({selected, vehicle: v, passengerCount, onSelect}: VehicleItemProps) {

    return (
        <div key={v.id} className={cn(css.vehicleItem, selected && css.vehicleSelected)}>
            <div className={css.carouselContainer}>
                <ImageCarousel images={v.images}/>
            </div>

            <div className={css.vehicleInfoContainer}>
                <div className={css.vehicleInfoRight}>
                    <div className={css.vehicleInfoTop}>
                        <div className={css.vehicleName}>{v.name}</div>
                        <div>
                            {v.capacity && v.capacity != 0 ? <span
                                className={cn([css.vehicleCapacity, passengerCount && passengerCount > v.capacity && css.overCapacity])}
                                title={`A max of ${v.capacity} passengers can fit comfortably in this vehicle`}>
                                            <Icon
                                                name={icons.faUserFriends}/>
                                            <span>{v.capacity} Passengers</span>
                                        </span> : null}
                        </div>
                        {v.features?.length ?
                        <div>
                            <ul className={css.featureList}>
                                {arrayMap(v.features, feat => <li key={feat.id}>
                                    <Icon name={icons.miscCheck} color="#8e9d8e"/> {feat.name}
                                </li>)}
                            </ul>
                        </div> : null}
                    </div>
                    {!v.hourlyRate ? null :
                        <div className={css.vehicleInfoPriceContainer}>
                            <HourlyRate {...v.hourlyRate}/>
                        </div>
                    }
                </div>
                {!onSelect ? null :
                    <div>
                        <ActionButton disabled={v.unavailable}
                                      className={[selected ? 'btn-save' : "btn-new", css.selectVehicleButton]}
                                      onClick={() => onSelect(v.id)}>
                            {selected ? <><Icon
                                    name={icons.miscCheck}/> Selected</>
                                : (v.unavailable ? <><Icon
                                    name={icons.faTimesCircleHallow}/> Unavailable</> : <><Icon
                                    name={icons.miscRightArrow}/> Select</>)}</ActionButton>
                    </div>
                }
            </div>
        </div>
    )
}

function VehicleSelection({companyCode}: VehicleSelectionProps) {
    const store = formState.useFormStore()
    const passengerCount = parseInt(store.get('passenger_count'), 0)

    const segments = useMemo(() => mapDefined(store.data.segments, seg => {
        const pickupDateTime = combineDateAndTime(seg.date, seg.time)
        if(!pickupDateTime) return null
        return {
            pickupTimeIso: pickupDateTime.toISOString(),
            duration: parseInt(seg.duration, 0),
        }
    }), [store.data.segments])

    const vehicleQuery = useAvailableVehicles({
        companyCode: companyCode,
        segments,
        capacity: store.get('passenger_count'),
        tripType: store.get('trip_type'),
    }, {
        keepPreviousData: true,
        enabled: segments.length > 0,
    })

    const selectVehicle = (vehicleId: number) => {
        store.set('vehicle_id', vehicleId)
        // FIXME: this is probably not the best way to keep these 2 things in sync, but I don't know how to read of
        // React Query's cache
        currentVehicleStore.setState(vehicleQuery.data?.vehicles.find(v => v.id == vehicleId))
    }

    return (
        <ContentSection title="Vehicle Selection">
            {!segments.length ?
                <p>Please choose a Date and Start Time for your segments under &ldquo;Trip Itinerary&rdquo; so we can
                    check what vehicles are available</p> : <>
                    {vehicleQuery.isLoading ? <LoadingDots height={200}/> :
                        (!vehicleQuery.data?.vehicles?.length ?
                            <p>No vehicles available</p>
                            : <div className={css.vehicleGrid}>
                                {arrayMap(vehicleQuery.data.vehicles, v => <VehicleItem key={v.id} vehicle={v}
                                                                                        selected={store.get('vehicle_id') == v.id}
                                                                                        onSelect={selectVehicle}
                                                                                        passengerCount={passengerCount}/>)}
                            </div>)
                    }</>}
        </ContentSection>
    )
}

function ReviewStop({stop, number}: { stop: StopModel, number: string }) {
    return (
        <div className={cpp.stopContainer}>
            <span className={cpp.stopNumber}>{number}:</span>
            <span className={cpp.stopAddress}>{formatAddress(stop) ||
                <span className="not-set">Not Specified</span>}</span>
        </div>
    )
}

function FlightBox(flight: FlightInfo) {
    if(!flight.number) return null

    return (
        <span className={css.vehicleCapacity}>
            <Icon
                name={flight.departure === 'DEPARTURE' ? icons.faPlaneDeparture : (flight.departure === 'ARRIVAL' ? icons.faPlaneArrival : icons.faPlane)}/>
            <span>{flight.number}</span>
            <span>{flight.domestic === 'DOMESTIC' ? `(Domestic)` : (flight.domestic === 'INTERNATIONAL' ? `(International)` : null)}</span>
        </span>
    )
}

function BagBox({small, medium, large}: SegmentBagsValue) {
    if(!small && !medium && !large) return null

    return (
        <span className={css.vehicleCapacity}>
            <Icon name={icons.faSuitcaseRolling}/>
            {small ? <span><b>S:</b>{small}</span> : null}
            {medium ? <span><b>M:</b>{medium}</span> : null}
            {large ? <span><b>L:</b>{large}</span> : null}
        </span>
    )
}

function ReviewSegment({segment: seg, number}: { segment: SegmentModel, number: number }) {
    const pickupDate = combineDateAndTime(seg.date, seg.time)
    const duration = parseInt(seg.duration)

    let dropoffDate: Date | null = null
    if(pickupDate && duration) {
        dropoffDate = addMinutes(duration, pickupDate)
    }

    return (
        <div className={cpp.segmentContainer}>
            <div className={cpp.segmentNumber}>S{number}</div>
            <div className={cpp.segmentDetails}>
                <div>{pickupDate ? formatTime(pickupDate) : null}{dropoffDate ? <> &ndash; {formatTime(dropoffDate)}</> : null}</div>
                {!isEmptyInput(seg.flight) || !isEmptyInput(seg.bags) ?
                    <div className={cpp.iconRow}>
                        <FlightBox {...seg.flight}/>
                        <BagBox {...seg.bags}/>
                    </div> : null}
                <div>
                    {arrayMap(seg.stops, (stop, stopIdx) => <ReviewStop key={stop.key ?? `idx-${stopIdx}`} stop={stop}
                                                                        number={`${number}${columnName(stopIdx)}`}/>)}
                </div>
                {!isEmptyInput(seg.comments) ? <div>{seg.comments}</div> : null}
            </div>
        </div>
    )
}


function TripReview({segments}: { segments: SegmentModel[] }) {
    if(!segments?.length) return <p>Not specified.</p>

    const groupedSegs = groupBy(segments, s => s.date ? reformatDate(s.date, 'EEE, dd LLL yyyy') : '')

    let segNumber = 1
    return (
        <div className={cpp.segmentsContainer}>
            {mapMap(groupedSegs, (segGroup, date) => {
                return (
                    <div key={date}>
                        <div className={cpp.dateRow}>{date || "No Date"}</div>
                        <div className={cpp.segGroupContainer}>
                            {segGroup.map((seg, idx) => <ReviewSegment key={seg.key ?? seg.id ?? `idx-${idx}`}
                                                                       segment={seg} number={segNumber++}/>)}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

interface InstantQuoteContentsProps {
    companyCode: string
}

type LineItem = {
    name: string
    amount: DollarAmount
}

type Fee = LineItem & {id: number}

type Billing = {
    base: LineItem
    fees: Fee[]
    total: DollarAmount
}

type InstantQuoteRequest = {
    company_code: string
} & FormDataType

type InstantQuoteResponse = XOR<{enabled:false}|{
    enabled: true,
    billing: Billing
}>



function InstantQuoteContents({companyCode}: InstantQuoteContentsProps) {
    const priceData = formState.useState(fpPick(['segments','trip_type','vehicle_id','extras']))

    const {loading,data,error} = usePostServerDataDebounced<InstantQuoteRequest,InstantQuoteResponse>('bookings.getInstantQuote', {
        ...priceData,
        company_code: companyCode,
    })

    if(loading) {
        return <LoadingDots height={200}/>
    }

    if(error) {
        return <ErrorText>We couldn't auto-calculate a price for your request. Please double check your inputs and if they look correct Submit your request and we will get back to you with a quote.</ErrorText>
    }

    if(!data?.enabled) {
        return <ErrorText>Instant quote is not available for this booking. Please submit your request and we will get back to you with a quote.</ErrorText>
    }

    return (
        <table className={css.billingTable}>
            <tbody>
                <tr>
                    <td className={css.liName}>{data.billing.base.name}</td>
                    <td className={css.liAmount}>{data.billing.base.amount.formatted}</td>
                </tr>
            </tbody>
            <tbody>
                {data.billing.fees.map(fee => (
                    <tr key={fee.id}>
                        <td className={css.liName}>{fee.name}</td>
                        <td className={css.liAmount}>{fee.amount.formatted}</td>
                    </tr>
                ))}
            </tbody>
            <tbody>
                <tr className={css.grandTotal}>
                    <td className={css.liName}>Grand Total</td>
                    <td className={css.liAmount}>{data.billing.total.formatted}</td>
                </tr>
            </tbody>
        </table>
    )
}

interface ReviewProps {
    occasions: OptionTuples
    phoneTypes: OptionTuples
    companyCode: string
    instantQuoteEnabled: boolean
    disclaimer: string
    optionalFees: OptionalFee[]
}

// FIXME: doesn't work with the .toJSON thing
// function CustomerDetailsTable(customer: CustomerInfo) {
//     return (
//         <table className={css.detailsTable}>
//             <tbody>
//                 <tr>
//                     <th><Icon name={icons.faUser}/> Name</th>
//                     <td>{makeFullName(customer.first_name, customer.last_name) ||
//                         <NotSetText>Blank</NotSetText>}</td>
//                 </tr>
//                 <tr>
//                     <th><Icon name={icons.faBuilding}/> Company</th>
//                     <td>{customer.company || <NotSetText>Blank</NotSetText>}</td>
//                 </tr>
//                 <tr>
//                     <th><Icon name={icons.faEnvelope}/> Email</th>
//                     <td>{customer.email || <NotSetText>Blank</NotSetText>}</td>
//                 </tr>
//                 <tr>
//                     <th><Icon name={icons.faPhoneAlt}/> Primary Phone</th>
//                     <td>{customer.pri_phone_number ? <>{customer.pri_phone_number} ({customer.pri_phone_type})</> : <NotSetText>Blank</NotSetText>}</td>
//                 </tr>
//                 <tr>
//                     <th><Icon name={icons.faPhoneAlt}/> Secondary Phone</th>
//                     <td>{customer.sec_phone_number ? <>{customer.sec_phone_number} ({customer.sec_phone_type})</> : <NotSetText>Blank</NotSetText>}</td>
//                 </tr>
//             </tbody>
//         </table>
//     )
// }




function Review({occasions, phoneTypes, companyCode, instantQuoteEnabled, disclaimer, optionalFees}: ReviewProps) {
    const store = formState.useFormStore()
    const occasionMap = useMemo(() => idMap(occasions), [occasions])
    const phoneMap = useMemo(() => new Map(phoneTypes), [phoneTypes])
    const vehicle = currentVehicleStore.useState()

    return (
        <>
            {/*<ContentSection title="Debug">*/}
            {/*    <pre>{varDump(store.data)}</pre>*/}
            {/*</ContentSection>*/}
            <ContentSection title="Trip Itinerary">
                <TripReview segments={store.get('segments', [])}/>
            </ContentSection>
            <ContentSection title="Trip Details">
                <table className={css.detailsTable}>
                    <tbody>
                        <tr>
                            <th><Icon name={icons.miscMapMarker}/> Trip Type</th>
                            <td>{TRIP_TYPE_MAP.get(store.get('trip_type') ?? '') ??
                                <NotSetText>Not Selected</NotSetText>}</td>
                        </tr>
                        <tr>
                            <th><Icon name={icons.faStar}/> Occasion</th>
                            <td>{occasionMap.get(Number(store.get('occasion_id'))) ??
                                <NotSetText>Not Selected</NotSetText>}</td>
                        </tr>
                        <tr>
                            <th><Icon name={icons.miscPassengers}/> Total Passengers</th>
                            <td>{store.get('passenger_count') ? numberFormat(store.get('passenger_count')) :
                                <NotSetText>Not Selected</NotSetText>}</td>
                        </tr>
                    </tbody>
                </table>
            </ContentSection>
            <ContentSection title="Vehicle">
                {!vehicle ? <NotSetText>Not Selected</NotSetText> : <VehicleItem vehicle={vehicle} />}
            </ContentSection>
            <ContentSection title="Your Information">
                <table className={css.detailsTable}>
                    <tbody>
                        <tr>
                            <th><Icon name={icons.faUser}/> Name</th>
                            <td>{makeFullName(store.get('customer.first_name'), store.get('customer.last_name')) ||
                                <NotSetText>Blank</NotSetText>}</td>
                        </tr>
                        <tr>
                            <th><Icon name={icons.faBuilding}/> Company</th>
                            <td>{store.get('customer.company') || <NotSetText>Blank</NotSetText>}</td>
                        </tr>
                        <tr>
                            <th><Icon name={icons.faEnvelope}/> Email</th>
                            <td>{store.get('customer.email') || <NotSetText>Blank</NotSetText>}</td>
                        </tr>
                        <tr>
                            <th><Icon name={icons.faPhoneAlt}/> Primary Phone</th>
                            <td>{store.get('customer.pri_phone_number') ? <>{store.get('customer.pri_phone_number')} ({phoneMap.get(store.get('customer.pri_phone_type'))})</> : <NotSetText>Blank</NotSetText>}</td>
                        </tr>
                        <tr>
                            <th><Icon name={icons.faPhoneAlt}/> Secondary Phone</th>
                            <td>{store.get('customer.sec_phone_number') ? <>{store.get('customer.sec_phone_number')} ({phoneMap.get(store.get('customer.sec_phone_type'))})</> : <NotSetText>Blank</NotSetText>}</td>
                        </tr>
                    </tbody>
                </table>
                {store.get('passenger_copy') !== 'DIFFERENT' ? <p>You are also the passenger.</p> : null}
            </ContentSection>
            {store.get('passenger_copy') === 'DIFFERENT' ? <ContentSection title="Passenger Info">
                <table className={css.detailsTable}>
                    <tbody>
                        <tr>
                            <th><Icon name={icons.faUser}/> Name</th>
                            <td>{makeFullName(store.get('passenger_first_name'), store.get('passenger_last_name')) ||
                                <NotSetText>Blank</NotSetText>}</td>
                        </tr>
                        <tr>
                            <th><Icon name={icons.faPhoneAlt}/> Primary Phone</th>
                            <td>{store.get('passenger_pri_phone_number') ? <>{store.get('passenger_pri_phone_number')} ({phoneMap.get(store.get('passenger_pri_phone_type'))})</> : <NotSetText>Blank</NotSetText>}</td>
                        </tr>
                        <tr>
                            <th><Icon name={icons.faPhoneAlt}/> Secondary Phone</th>
                            <td>{store.get('passenger_sec_phone_number') ? <>{store.get('passenger_sec_phone_number')} ({phoneMap.get(store.get('passenger_sec_phone_type'))})</> : <NotSetText>Blank</NotSetText>}</td>
                        </tr>
                    </tbody>
                </table>
            </ContentSection> : null}

            {optionalFees?.length ? <ContentSection title="Extras">
                <SimpleTable style={{width:'auto'}}>
                    <thead>
                        <tr>
                            <th style={{width:200}}>Item</th>
                            <th style={{width:100}}>Price</th>
                            <th style={{width:120}}>Quantity</th>
                        </tr>
                    </thead>
                    <tbody>
                    {optionalFees.map(fee => <tr key={fee.id}>
                        <td className="vcenter">
                            {fee.name}
                        </td>
                        <td className="tright vcenter">
                            {fee.price}
                        </td>
                        <td className="vcenter">
                            {fee.type === 'FIXED' && fee.maxQuantity != 1 ? <NumberBox min={0} step={1} max={fee.maxQuantity} {...store.input(['extras',fee.id])} /> :
                                <AioCheckbox label="Include" {...store.checkbox(['extras',fee.id])} />}
                        </td>

                    </tr>)}
                    </tbody>
                </SimpleTable>
            </ContentSection> : null}

            {instantQuoteEnabled ? <ContentSection title="Quote">
                <InstantQuoteContents companyCode={companyCode}/>
            </ContentSection> : null}

            {disclaimer ? <ContentSection title="Disclaimer">
                <Markup content={disclaimer}/>
            </ContentSection> : null}

            <ContentSection title="Comments">
                <p>Do you have any special requests, questions or concerns?</p>
                <AioTextArea label="Comments" {...store.input('notes')}/>
            </ContentSection>
        </>
    )
}


type PageInfo = {
    title: string
    key: string
    validate?: (state: FormDataType, props: FreeQuoteFormProps) => ValidationErrors
}

const PAGES = [
    {
        key: 'trip_itinerary',
        title: "Trip Itinerary",
        validate(state: FormDataType, props: FreeQuoteFormProps) {
            const errors = []
            if(props.instantQuoteEnabled && ! state.trip_type) {
                errors.push("Trip Type is required")
            }
            let segNum = 1;
            for(const seg of state.segments) {
                if(state.trip_type === 'AS_DIRECTED' && !seg.duration) {
                    errors.push(`Duration is required for Segment ${segNum}`)
                }
                if(!seg.date || !parseDate(seg.date)) {
                    errors.push(`Date is required for Segment ${segNum}`)
                }

                if(!seg.time) {
                    errors.push(`Start Time is required for Segment ${segNum}`)
                } else {
                    const [hr,min] = parseTime(seg.time)
                    if(hr == null) {
                        errors.push(`Start Time hour is required for Segment ${segNum}`)
                    } else if(min == null) {
                        errors.push(`Start Time minute is required for Segment ${segNum}`)
                    }
                }
                let stopIdx = 0;
                for(const stop of seg.stops) {
                    const formattedStop = formatBookingStop(stop,stopIdx,seg.stops.length)
                    if(!stopIsValid(formattedStop)) {
                        errors.push(`Valid address is required for stop ${segNum}${formattedStop.letter}. If you entered an address and are still receiving this error, please press "Turn Autocomplete Off" and if all the required fields are filled.`)
                    }
                    ++stopIdx;
                }
            }
            ++segNum;
            return errors
        }
    },
    {
        key: 'vehicle',
        title: "Vehicle Selection",
        validate(state: FormDataType, props: FreeQuoteFormProps) {
            const errors = []
            if(!state.vehicle_id) {
                errors.push("Please select a vehicle")
            }
            return errors
        }
    },
    {
        key: 'contact_info',
        title: "Contact Info",
        validate(state: FormDataType, props: FreeQuoteFormProps) {
            const errors = []
            if(!state.customer.first_name && !state.customer.last_name) {
                errors.push("First or last name is required")
            }
            if(!state.customer.email) {
                errors.push("Email is required")
            } else if(!EMAIL_REGEX.test(state.customer.email)) {
                errors.push("Email is invalid")
            }
            return errors
        }
    },
    {
        key: 'review',
        title: "Summary",
    },

] as const satisfies readonly PageInfo[]

type PageKey = typeof PAGES[number]['key']

interface SubmitResponseData {
    confirmationCode: string
    redirectUrl: string|null
    errors?: ValidationErrors
}

function getIsInIFrame () {
    // https://stackoverflow.com/questions/326069/how-to-identify-if-a-webpage-is-being-loaded-inside-an-iframe-or-directly-into-t
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
}

export default function FreeQuoteForm(props: FreeQuoteFormProps) {
    const [pageIdx, setPageIdx] = useState(0)

    useMountEffect(() => {
        if(props.customer) {
            formState.setState(state => ({
                ...state,
                customer: props.customer!,
            }))
        }
    })

    const pageKey = PAGES[pageIdx].key
    assert(pageKey)
    const pageMap: Record<PageKey, ReactElement> = {
        trip_itinerary: <TripItinerary googleMapsApiKey={props.googleMapsApiKey}
                                       distanceUnit={props.distanceUnit}
                                       occasions={props.occasions}/>,
        vehicle: <VehicleSelection companyCode={props.companyCode}/>,
        contact_info: <ContactInfo phoneTypes={props.phoneTypes} existingCustomer={!!props.existingCustomerId}/>,
        review: <Review occasions={props.occasions} phoneTypes={props.phoneTypes} companyCode={props.companyCode} instantQuoteEnabled={props.instantQuoteEnabled} disclaimer={props.disclaimer} optionalFees={props.optionalFees}/>,
    }
    const pageSection = pageMap[pageKey]
    assert(pageSection)

    const navBarRef = useRef<HTMLDivElement | null>(null)

    const scrollUp = () => {
        setTimeout(() => {  // wait for page to render
            navBarRef.current?.scrollIntoView?.({
                behavior: 'auto',
                block: 'nearest',
                inline: 'nearest',
            })
        }, 0)
    }

    const goToPage = (n: number) => {
        const validate = (PAGES[pageIdx] as PageInfo).validate
        if(n > pageIdx && validate) {
            const errors = validate(formState.getSnapshot(), props)
            if(errors?.length) {
                setValidationErrors(errors)
                return
            }
            setValidationErrors(null)
        }
        setPageIdx(n)
        scrollUp()
    }
    const goPrevPage = () => goToPage(pageIdx-1)
    const goNextPage = () => goToPage(pageIdx+1)

    const [validationErrors, setValidationErrors] = useState<ValidationErrors | null>(null)
    const [confirmationCode, setConfirmationCode] = useState<string | null>(null)
    const [isRedirecting, setIsRedirecting] = useState(false)

    const [submit, submitting] = useSaveHandler<SubmitResponseData>({
        route: 'quotes.submit',
        data: () => ({
            ...formState.getSnapshot(),
            company_code: props.companyCode,
            customer_id: props.existingCustomerId,
        }),
        success: res => {
            assert(res.data.confirmationCode)
            setValidationErrors(null)
            setConfirmationCode(res.data.confirmationCode)
            if(!props.instantPayEnabled || !res.data.redirectUrl) {
                window.parentIFrame?.sendMessage?.({
                    event: 'submit-success',
                    confirmationCode: res.data.confirmationCode,
                })
            }
            if(res.data.redirectUrl) {
                setIsRedirecting(true)
                navigate(res.data.redirectUrl)
            }
        },
        error: res => {
            if(res.data?.errors) {
                setValidationErrors(res.data.errors)
            } else {
                kb.notify({
                    text: "An unknown error occurred",
                    className: "error",
                })
            }
        }
    })

    if(confirmationCode) {
        return (
            <div className={css.thanksPage}>
                <div className={css.thanksContainer}>
                    <h3 className={css.thanksBig}>Thank You!</h3>
                    {isRedirecting
                        ? <p>Redirecting...</p>
                        : <p className={css.thanksInfo}>We will get back to you shortly with your quote.</p>
                    }
                </div>
            </div>
        )
    }

    const isIFrame = getIsInIFrame()

    return (
        <GridForm className={css.pageWrap}>
            {isIFrame ? null : <ContentIndent>
                <CompanyBlock {...props.company}/>
            </ContentIndent>}

            <div className={css.wizardProgressBar} ref={navBarRef}>
                {PAGES.map((page, idx) => (
                    <div key={page.key} className={css.wizardItem}>
                        <ActionLink disabled={pageIdx === idx}
                                    className={cn([css.wizardLink, pageIdx === idx && css.wizardItemCurrent])}
                                    onClick={() => goToPage(idx)}>{page.title}</ActionLink>
                    </div>
                ))}
            </div>

            {pageSection}

            {!validationErrors ? null : <>
                <ContentIndent>
                    <div className="notify-message error">
                        <FlattenedErrorMessage errors={validationErrors}/>
                    </div>
                </ContentIndent>
                <BlockSpacer height={10}/>
            </>}

            <ContentIndent>
                <div className="button-row-flex">
                    <div>
                        <ActionButton className="btn-large" disabled={pageIdx <= 0}
                                      onClick={goPrevPage}><Icon name={icons.ionArrowLeftB}/>Prev Page</ActionButton>
                    </div>
                    {pageKey === 'review' ? <div>
                        <SpinnerButton className="btn-save btn-large" icon={icons.faPaperPlane} onClick={submit}
                                       processing={submitting}>Submit</SpinnerButton>
                    </div> : null}
                    <div>
                        <ActionButton className="btn-large" disabled={pageIdx >= PAGES.length - 1}
                                      onClick={goNextPage}>Next Page<Icon
                            name={icons.ionArrowRightB}/></ActionButton>
                    </div>
                </div>
            </ContentIndent>

            <ContentIndent>
                <HelpText><span className={aioCss.requiredAsterisk}>*</span> Required input.</HelpText>
            </ContentIndent>
        </GridForm>
    )
}
